// Same-origin by default: the Cloudflare Worker serves both the site and /api.
export const API_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

import { Instagram, Facebook, Linkedin } from "lucide-react";

export const SOCIALS = [
  {
    name: "Instagram",
    label: "ABS Motorsport Concierge",
    href: "https://www.instagram.com/alextouring/",
    aria: "ABS Motorsport Concierge on Instagram",
    icon: Instagram,
  },
  {
    name: "Facebook",
    label: "ABS Motorsport Concierge",
    href: "https://www.facebook.com/alextouring/",
    aria: "ABS Motorsport Concierge on Facebook",
    icon: Facebook,
  },
  {
    name: "LinkedIn",
    label: "Alex Brosseau | Founder & Driver Coach",
    href: "https://ca.linkedin.com/in/alexbrosseau",
    aria: "Alex Brosseau on LinkedIn",
    icon: Linkedin,
  },
];

export const CONTACT = {
  email: "Alextouring@gmail.com",
  baseUS: "Venice, FL, USA",
  baseCA: "Montreal, QC, Canada",
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/driver-development", label: "Driver Development" },
  { to: "/about", label: "About" },
  { to: "/events-operations", label: "Events & Operations", short: "Events" },
  { to: "/sebring-garage", label: "Sebring Garage" },
  { to: "/recaps", label: "Recaps" },
  { to: "/gallery", label: "Gallery" },
  { to: "/partners", label: "Partners" },
  { to: "/contact", label: "Contact" },
];

export const IMAGES = {
  heroTrack:
    "https://images.unsplash.com/photo-1740784771841-6d2188d21f20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxtb3RvcnNwb3J0JTIwcGl0JTIwbGFuZSUyMHRyYWNrc2lkZXxlbnwwfHx8fDE3ODY5OTY4NDd8MA&ixlib=rb-4.1.0&q=85",
  bmwTrack:
    "https://images.unsplash.com/photo-1776231491638-93a4f2a6a30e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxwZXJmb3JtYW5jZSUyMHJhY2UlMjBjYXIlMjBvbiUyMHRyYWNrfGVufDB8fHx8MTc4Njk5Njg1M3ww&ixlib=rb-4.1.0&q=85",
  pitLane:
    "https://images.pexels.com/photos/29255759/pexels-photo-29255759.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  duel:
    "https://images.pexels.com/photos/28944070/pexels-photo-28944070.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  driverFocus:
    "https://images.pexels.com/photos/158967/pexels-photo-158967.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  teamBriefing: "/images/team-briefing.webp",
  teamPitwall: "/images/team-pitwall.webp",
  driverSuit: "/images/driver-suit.webp",
  teamCanopy: "/images/team-canopy.webp",
  teamFamily: "/images/team-family.webp",
  teamFirst: "/images/team-1st.webp",
  teamSecond: "/images/team-2nd.webp",
  teamThird: "/images/team-3rd.webp",
  crewChief: "/images/crew-chief.webp",
  rolexClock: "/images/rolex-clock.jpg",
  car134: "/images/t1-alex.webp",
  teamCar649: "/images/go-racing-pitt.webp",
  simRigs: "/images/simulator.webp",
  tradeShow: "/images/show-miata.webp",
};

export const SERVICES = [
  {
    num: "01",
    title: "Arrive-and-Drive Race Programs",
    short: "Show up with your helmet. Everything else is already handled.",
    text: "A complete turnkey race weekend: car preparation, transport, setup, crew, and spares are in place before you reach the paddock. You brief with your engineer, belt in, and drive.",
    points: ["Full weekend program management", "Car prep, setup and spares packages", "Crew, engineering and data support"],
  },
  {
    num: "02",
    title: "Driver Development & Coaching",
    short: "Structured progress, on track and off it.",
    text: "One-to-one coaching built around your goals — from first track days to competitive race craft. Programs blend seat time, data review, and honest debriefs that turn laps into learning.",
    points: ["Personal coaching plans", "Video and data debriefs", "Race craft and test-day structure"],
  },
  {
    num: "03",
    title: "Race Logistics & Vehicle Transport",
    short: "The right car, parts, and people — at the right circuit, on time.",
    text: "End-to-end movement of race cars, equipment, and consumables across North America. Scheduling, routing, and paddock placement are coordinated so nothing is left to chance.",
    points: ["Enclosed vehicle transport", "Parts and equipment coordination", "Multi-event season scheduling"],
  },
  {
    num: "04",
    title: "Trackside Operational Support",
    short: "A professional pit presence at every session.",
    text: "Tire management, fueling, pit equipment, timing, and session scheduling run by an experienced crew. The paddock runs like a works team, whatever the class.",
    points: ["Pit lane and paddock setup", "Tire, fuel and stint management", "Session timing and scheduling"],
  },
  {
    num: "05",
    title: "Performance Optimization",
    short: "The whole athlete, the whole package.",
    text: "Physical training integration, simulator development, mental preparation, race strategy, and performance analysis — unified into one program that measurably moves the stopwatch.",
    points: ["Physical training integration", "Simulator development programs", "Mental prep, strategy and analysis"],
  },
];

export const WORKFLOW = [
  { num: "01", title: "Consultation", text: "We map your goals, calendar, car, and budget into a single program plan." },
  { num: "02", title: "Preparation", text: "Logistics, transport, car prep, and crew are locked in well before load-in." },
  { num: "03", title: "Execution", text: "Trackside, we run the operation — sessions, strategy, tires, fuel, data." },
  { num: "04", title: "Debrief", text: "Post-event analysis and next-step development, so every weekend compounds." },
];

export const DEV_PILLARS = [
  { num: "01", title: "Coaching & Mentoring", text: "One-to-one driver coaching with clear objectives per session and per season." },
  { num: "02", title: "Physical Training Integration", text: "Strength, endurance, and heat-conditioning work aligned with the race calendar." },
  { num: "03", title: "Simulator Development", text: "Structured sim programs for circuit learning, reference laps, and procedure work." },
  { num: "04", title: "Mental Preparation", text: "Routines for focus, pressure, and consistency — qualifying lap after qualifying lap." },
  { num: "05", title: "Race Strategy", text: "Stint planning, traffic management, and decision frameworks for changing conditions." },
  { num: "06", title: "Performance Analysis", text: "Data and video review that turns every outing into a measurable step forward." },
];

export const CIRCUITS = [
  {
    name: "Road Atlanta",
    region: "Braselton, Georgia",
    text: "Fast, flowing, and unforgiving — Michelin Raceway's elevation changes reward meticulous preparation.",
  },
  {
    name: "Watkins Glen International",
    region: "Watkins Glen, New York",
    text: "A high-speed classic in the Finger Lakes. Long corners, big commitment, no margin for a sloppy operation.",
  },
  {
    name: "Sebring International Raceway",
    region: "Sebring, Florida",
    text: "Bumpy, brutal, legendary — the ultimate test of car preparation and endurance discipline.",
  },
  {
    name: "Virginia International Raceway",
    region: "Alton, Virginia",
    text: "Technical rhythm and tire management across one of America's finest natural-terrain road courses.",
  },
  {
    name: "Circuit of the Americas",
    region: "Austin, Texas",
    text: "A modern Grand Prix stage containing every corner type in the book.",
  },
];

export const GALLERY = [
  { src: IMAGES.teamBriefing, caption: "Pit lane briefing with the driver", tag: "ABS Team" },
  { src: IMAGES.teamPitwall, caption: "Calling the session from the pit wall", tag: "ABS Team" },
  { src: IMAGES.driverSuit, caption: "Suited up and ready to roll", tag: "ABS Team" },
  { src: IMAGES.teamCanopy, caption: "Trackside operations under the canopy", tag: "ABS Team" },
  { src: IMAGES.teamFamily, caption: "The whole crew — racing is a family affair", tag: "ABS Team" },
  { src: IMAGES.teamFirst, caption: "Victory Lane with the team", tag: "Results" },
  { src: IMAGES.teamSecond, caption: "Runner-up trophy, Road Atlanta", tag: "Results" },
  { src: IMAGES.teamThird, caption: "On the podium at Michelin Raceway", tag: "Results" },
  { src: IMAGES.crewChief, caption: "Trackside in the Watkins Glen paddock", tag: "ABS Team" },
  { src: IMAGES.rolexClock, caption: "The Rolex clock — Daytona", tag: "Circuits" },
  { src: IMAGES.teamCar649, caption: "The #649 with the full crew, post-race", tag: "ABS Team" },
  { src: IMAGES.car134, caption: "Belted in at Thunderbolt Raceway", tag: "Race Craft" },
  { src: IMAGES.simRigs, caption: "D-BOX haptic simulator development", tag: "Driver Development" },
  { src: IMAGES.tradeShow, caption: "Representing at the performance expo", tag: "ABS Team" },
  { src: IMAGES.duel, caption: "Wheel-to-wheel on circuit", tag: "Race Craft" },
  { src: IMAGES.driverFocus, caption: "Focus work between sessions", tag: "Driver Development" },
  { src: IMAGES.pitLane, caption: "Garage preparation, pit lane", tag: "Trackside" },
  { src: IMAGES.bmwTrack, caption: "Performance runs at speed", tag: "Arrive & Drive" },
  { src: IMAGES.heroTrack, caption: "Race weekends across North America", tag: "Circuits" },
];

export const LOCAL_IMAGES = [
  { src: "/images/team-1st.webp", label: "Victory Lane" },
  { src: "/images/team-2nd.webp", label: "Road Atlanta trophy" },
  { src: "/images/team-3rd.webp", label: "Road Atlanta podium" },
  { src: "/images/team-briefing.webp", label: "Pit briefing" },
  { src: "/images/team-pitwall.webp", label: "Pit wall" },
  { src: "/images/team-canopy.webp", label: "Canopy operations" },
  { src: "/images/team-family.webp", label: "Team trailer" },
  { src: "/images/crew-chief.webp", label: "Watkins Glen paddock" },
  { src: "/images/driver-suit.webp", label: "Driver suit" },
  { src: "/images/t1-alex.webp", label: "Car 134" },
  { src: "/images/go-racing-pitt.webp", label: "Car 649 crew" },
  { src: "/images/simulator.webp", label: "Simulator" },
  { src: "/images/show-miata.webp", label: "Expo Miata" },
  { src: "/images/rolex-clock.jpg", label: "Daytona Rolex" },
];

export const MARQUEE_ITEMS = [
  "Arrive & Drive",
  "Driver Development",
  "Race Logistics",
  "Trackside Support",
  "Performance Optimization",
  "North America",
];

export const SEBRING_GARAGE = {
  intro:
    "ABS LLC's new Sebring, Florida garage is a premium home base for your car, your program and your race weekend. From secure storage and race prep to enclosed transport and trackside support, we handle the details so you can focus on driving.",
  services: [
    { title: "Vehicle storage and care", text: "Secure indoor space for race and road cars, kept clean and ready." },
    { title: "Race prep and technical coordination", text: "Pre-event preparation, parts sourcing and coordination with your crew or service shop." },
    { title: "Enclosed transport", text: "Hauling between the garage, the track and your shop, with support logistics planned around your schedule." },
    { title: "Trailer and RV coordination", text: "Staging and support for trailers, motorhomes and paddock setups." },
    { title: "Client lounge and workspace", text: "A comfortable space for drivers, owners and families before and after sessions." },
    { title: "Event-week accommodation", text: "Short-term stays for drivers and team members, with garage space for their cars, during race weekends and other events at Sebring." },
    { title: "Trackside support", text: "Hands-on paddock help and client coordination throughout the event." },
  ],
  gallery: [
    { src: "/images/sebring-garage-01-overview.webp", alt: "ABS LLC Sebring garage: garage, storage and motorsport support", caption: "A premium Sebring base for vehicle care, race prep, storage and client support." },
    { src: "/images/sebring-garage-02-storage-hospitality.webp", alt: "ABS LLC Sebring garage: storage, staging and hospitality", caption: "Secure indoor storage, trailer and RV staging, a lounge for drivers and guests, and short-term stays for teams during events." },
    { src: "/images/sebring-garage-03-prep-transport.webp", alt: "ABS LLC race prep, parts and transport", caption: "Pre-event prep, technical coordination and enclosed transport to and from the track." },
    { src: "/images/sebring-garage-04-client-support.webp", alt: "ABS LLC trackside operations and client support", caption: "Hands-on trackside support for drivers, owners and families." },
  ],
};

export const PARTNERS = [
  {
    name: "Round 3 Racing",
    href: "https://www.round3racing.com/",
    domain: "round3racing.com",
    logo: "/images/partners/round-3-racing.webp",
    text: "A family-built race team out of Boca Raton, Florida, offering arrive-and-drive racing, a Junior Driver Program, and seats in the Zenith Racing Series and Mustang Challenge.",
  },
  {
    name: "HMS Motorsport",
    href: "https://www.hmsmotorsport.com/collections/hms",
    domain: "hmsmotorsport.com",
    logo: "/images/partners/hms-motorsport.webp",
    dark: true,
    text: "A leading motorsport safety equipment supplier to professional, amateur and first-time drivers, with showrooms in Mooresville, NC and Danvers, MA. HMS is the official safety equipment supplier to Historic Sportscar Racing (HSR).",
  },
  {
    name: "Candelaria Racing Products",
    href: "https://candelaria-racing.com/",
    domain: "candelaria-racing.com",
    logo: "/images/partners/candelaria-racing.webp",
    text: "Home of the Sentinel V3 motorsports video system, available to buy or rent for a race weekend, with remote setup and training.",
  },
  {
    name: "White Belt Racing Academy",
    href: "https://whitebeltracingacademy.com/",
    domain: "whitebeltracingacademy.com",
    logo: "/images/partners/white-belt-racing-academy.webp",
    text: "An Austin, Texas racing academy focused on getting its members on track, from COTA karting and track days to World Racing League endurance racing at Sebring and Octane Time Attack.",
  },
];
